import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Upload, FileText, Download, Eye, MessageSquare, History as HistoryIcon, Trash2, Loader2, Pencil } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { ProjectStagesTab } from "@/components/ProjectStagesTab";
import { formatDateTime, formatSize, STATUS_META, PROJECT_STATUS_META } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const isDono = user?.role === "CLIENT" && user?.client_role === "DONO";
  const [mainTab, setMainTab] = useState("cronograma");

  const [project, setProject] = useState(null);
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFor, setUploadFor] = useState({ category_id: "", document_id: "" });
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [detailDoc, setDetailDoc] = useState(null);
  const [docHistory, setDocHistory] = useState([]);
  const [docComments, setDocComments] = useState([]);
  const [docVersions, setDocVersions] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [statusForm, setStatusForm] = useState({ status_key: "", justification: "" });

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, cats, docs, sts] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get("/categories"),
        api.get("/documents", { params: { project_id: id } }),
        api.get("/statuses"),
      ]);
      setProject(pr.data);
      setCategories(cats.data);
      setDocuments(docs.data);
      setStatuses(sts.data);
    } catch (e) { toast.error("Erro ao carregar projeto"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const groupedDocs = () => {
    const map = {};
    for (const d of documents) {
      map[d.category_id] = map[d.category_id] || [];
      map[d.category_id].push(d);
    }
    return map;
  };

  const openUpload = (category_id, document_id = "") => {
    setUploadFor({ category_id, document_id });
    setUploadOpen(true);
    setUploadProgress(0);
  };

  const doUpload = async (files, extra = {}) => {
    const fileArray = Array.isArray(files) ? files : [files];
    if (fileArray.length === 0 || !fileArray[0]) return;
    setUploading(true); setUploadProgress(5);
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const fd = new FormData();
        fd.append("file", file);
        fd.append("project_id", id);
        fd.append("category_id", uploadFor.category_id);
        if (uploadFor.document_id) fd.append("document_id", uploadFor.document_id);
        if (extra.name) fd.append("name", extra.name);
        if (extra.notes) fd.append("notes", extra.notes);
        await api.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => { 
            if (evt.total) {
              const base = (i / fileArray.length) * 100;
              const current = (evt.loaded / evt.total) * (100 / fileArray.length);
              setUploadProgress(Math.round(base + current));
            }
          },
        });
        if (uploadFor.document_id) break; // Only upload one file if updating an existing document
      }
      toast.success(fileArray.length > 1 && !uploadFor.document_id ? "Arquivos enviados" : "Arquivo enviado");
      setUploadOpen(false);
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar"); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const openDoc = async (doc) => {
    setDetailDoc(doc);
    try {
      const [h, c, v] = await Promise.all([
        api.get(`/documents/${doc.id}/history`),
        api.get(`/documents/${doc.id}/comments`),
        api.get(`/documents/${doc.id}/versions`),
      ]);
      setDocHistory(h.data); setDocComments(c.data); setDocVersions(v.data);
    } catch (_e) { void _e; }
  };

  const download = (docId, inline = false) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/download?inline=${inline}&auth=${accessToken}`;
    if (inline) window.open(url, "_blank"); else window.location.href = url;
  };

  const addComment = async () => {
    if (!commentText.trim() || !detailDoc) return;
    try {
      const r = await api.post(`/documents/${detailDoc.id}/comments`, { text: commentText });
      setDocComments((p) => [...p, r.data]);
      setCommentText("");
    } catch { toast.error("Erro ao comentar"); }
  };

  const changeStatus = async () => {
    try {
      await api.post(`/documents/${detailDoc.id}/status`, statusForm);
      toast.success("Status alterado");
      setStatusChangeOpen(false);
      await loadAll();
      const r = await api.get(`/documents/${detailDoc.id}`); setDetailDoc(r.data);
      const h = await api.get(`/documents/${detailDoc.id}/history`); setDocHistory(h.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Excluir este documento?")) return;
    try { await api.delete(`/documents/${docId}`); toast.success("Removido"); setDetailDoc(null); await loadAll(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!uploadFor.category_id) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) doUpload(files[0]);
  };

  const openEditProject = () => {
    setEditForm({
      name: project.name || "",
      code: project.code || "",
      responsible: project.responsible || "",
      category: project.category || "",
      description: project.description || "",
      status: project.status || "em_andamento",
      start_date: project.start_date ? project.start_date.slice(0, 10) : "",
      due_date: project.due_date ? project.due_date.slice(0, 10) : "",
    });
    setEditOpen(true);
  };

  const saveEditProject = async () => {
    setEditSaving(true);
    try {
      const payload = { ...editForm };
      payload.start_date = payload.start_date ? new Date(payload.start_date).toISOString() : null;
      payload.due_date = payload.due_date ? new Date(payload.due_date).toISOString() : null;
      const r = await api.patch(`/projects/${id}`, payload);
      setProject(r.data);
      toast.success("Projeto atualizado");
      setEditOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar projeto"); }
    finally { setEditSaving(false); }
  };

  if (loading) return <Skeleton className="h-40 rounded-xl" />;
  if (!project) return <p>Projeto não encontrado.</p>;

  const projMeta = PROJECT_STATUS_META[project.status] || { label: project.status, color: "#64748B" };
  const grouped = groupedDocs();

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <button onClick={() => navigate("/projects")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-projects">
        <ArrowLeft size={14} /> Projetos
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-3xl font-bold" data-testid="project-name-heading">{project.name}</h1>
            <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border" style={{ color: projMeta.color, borderColor: `${projMeta.color}40`, backgroundColor: `${projMeta.color}15` }}>{projMeta.label}</span>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={openEditProject} className="gap-1.5" data-testid="edit-project-btn">
                <Pencil size={13} /> Editar
              </Button>
            )}
          </div>
          {project.code && <p className="font-mono text-xs text-muted-foreground mt-1">{project.code}</p>}
          {project.description && <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{project.description}</p>}
        </div>
        <div className="text-xs text-muted-foreground text-right">
          <p>Responsável: <span className="text-foreground">{project.responsible || "-"}</span></p>
          <p>Prazo: <span className="text-foreground">{project.due_date ? new Date(project.due_date).toLocaleDateString("pt-BR") : "-"}</span></p>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList data-testid="project-main-tabs">
          <TabsTrigger value="cronograma" data-testid="main-tab-cronograma">Cronograma</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="main-tab-documentos">Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="cronograma" className="mt-6">
          <ProjectStagesTab projectId={id} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="documentos" className="mt-6">
          <Tabs value={activeCat} onValueChange={setActiveCat}>
            <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 justify-start" data-testid="category-tabs">
              <TabsTrigger value="all" data-testid="cat-tab-all" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full text-xs">
                Todos ({documents.length})
              </TabsTrigger>
              {categories.map((c) => {
                const count = grouped[c.id]?.length || 0;
                const catColor = c.color;
                return (
                  <TabsTrigger key={c.id} value={c.id} data-testid={`cat-tab-${c.id}`}
                    className="rounded-full text-xs transition-all"
                    style={activeCat === c.id && catColor ? { backgroundColor: catColor, color: '#fff', borderColor: catColor } : catColor ? { borderColor: catColor, borderWidth: '1.5px', color: catColor } : {}}>
                    {catColor && <span className="w-2 h-2 rounded-full inline-block mr-1 shrink-0" style={{ backgroundColor: catColor }} />}
                    {c.name} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value="all" className="mt-6">
              <CategoryView docs={documents} categories={categories} isAdmin={isAdmin} openDoc={openDoc} download={download} openUpload={openUpload} />
            </TabsContent>
            {categories.map((c) => (
              <TabsContent key={c.id} value={c.id} className="mt-6">
                <div
                  className={`dropzone p-8 mb-4 text-center ${dragOver ? "is-active" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { setUploadFor({ category_id: c.id, document_id: "" }); onDrop(e); }}
                  data-testid={`dropzone-${c.id}`}
                >
                  <Upload className="mx-auto mb-2 text-muted-foreground" size={22} />
                  <p className="text-sm">Arraste um arquivo para <b>{c.name}</b> ou <button onClick={() => openUpload(c.id)} className="text-primary underline" data-testid={`upload-btn-${c.id}`}>selecione manualmente</button></p>
                </div>
                <CategoryView docs={grouped[c.id] || []} categories={categories} isAdmin={isAdmin} openDoc={openDoc} download={download} openUpload={openUpload} onlyCat={c} />
              </TabsContent>
            ))}
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{uploadFor.document_id ? "Nova versão" : "Enviar documento"}</DialogTitle></DialogHeader>
          <input type="file" ref={fileInputRef} multiple={!uploadFor?.document_id} onChange={(e) => e.target.files?.length && doUpload(Array.from(e.target.files))} className="hidden" data-testid="upload-file-input" />
          <div className="space-y-3">
            <Button className="w-full" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-choose-btn">
              {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando… {uploadProgress}%</> : "Escolher arquivo"}
            </Button>
            {uploading && <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} /></div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document detail dialog */}
      <Dialog open={!!detailDoc} onOpenChange={(o) => !o && setDetailDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3 flex-wrap" data-testid="doc-detail-title">
                  <FileText size={18} /> {detailDoc.name}
                  <StatusBadge statusKey={detailDoc.status_key} />
                </DialogTitle>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Arquivo">{detailDoc.filename}</Info>
                <Info label="Tamanho">{formatSize(detailDoc.size)}</Info>
                <Info label="Criado">{formatDateTime(detailDoc.created_at)}</Info>
                <Info label="Atualizado">{formatDateTime(detailDoc.updated_at)}</Info>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => download(detailDoc.id, true)} data-testid="doc-preview-btn"><Eye size={14} className="mr-1" /> Pré-visualizar</Button>
                <Button variant="outline" size="sm" onClick={() => download(detailDoc.id, false)} data-testid="doc-download-btn"><Download size={14} className="mr-1" /> Download</Button>
                <Button variant="outline" size="sm" onClick={() => openUpload(detailDoc.category_id, detailDoc.id)} data-testid="doc-new-version-btn"><Upload size={14} className="mr-1" /> Nova versão</Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => { setStatusForm({ status_key: detailDoc.status_key, justification: "" }); setStatusChangeOpen(true); }} data-testid="doc-change-status-btn">Alterar status</Button>
                )}
                {(isAdmin || (isDono && detailDoc.status_key === "recebido")) && (
                  <Button variant="ghost" size="sm" onClick={() => deleteDoc(detailDoc.id)} className="text-brand-red hover:text-brand-red" data-testid="doc-delete-btn"><Trash2 size={14} className="mr-1" /> Excluir</Button>
                )}
              </div>

              <Tabs defaultValue="history" className="mt-4">
                <TabsList>
                  <TabsTrigger value="history" data-testid="tab-history"><HistoryIcon size={13} className="mr-1" /> Linha do tempo</TabsTrigger>
                  <TabsTrigger value="comments" data-testid="tab-comments"><MessageSquare size={13} className="mr-1" /> Comentários</TabsTrigger>
                  <TabsTrigger value="versions" data-testid="tab-versions">Versões ({docVersions.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="history" className="mt-3">
                  <ul className="space-y-3">
                    {docHistory.map((h) => (
                      <li key={h.id} className="text-sm flex gap-3">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary shrink-0" />
                        <div className="flex-1">
                          <p>{h.description}</p>
                          <p className="text-xs text-muted-foreground">{h.user_name} • {formatDateTime(h.created_at)}</p>
                        </div>
                      </li>
                    ))}
                    {docHistory.length === 0 && <p className="text-sm text-muted-foreground">Sem histórico.</p>}
                  </ul>
                </TabsContent>
                <TabsContent value="comments" className="mt-3">
                  <ul className="space-y-3 mb-3">
                    {docComments.map((c) => (
                      <li key={c.id} className="p-3 rounded-lg bg-muted/60 text-sm">
                        <p>{c.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">{c.author_name} • {formatDateTime(c.created_at)}</p>
                      </li>
                    ))}
                    {docComments.length === 0 && <p className="text-sm text-muted-foreground">Nenhum comentário ainda.</p>}
                  </ul>
                  <div className="flex gap-2">
                    <Textarea rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Escreva um comentário…" data-testid="comment-input" />
                    <Button onClick={addComment} disabled={!commentText.trim()} data-testid="comment-submit-btn">Enviar</Button>
                  </div>
                </TabsContent>
                <TabsContent value="versions" className="mt-3">
                  <ul className="divide-y">
                    {docVersions.map((v) => (
                      <li key={v.id} className="py-3 flex items-center gap-3 text-sm">
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-muted">v{v.version_number}</span>
                        <span className="flex-1 truncate">{v.filename}</span>
                        <span className="text-xs text-muted-foreground">{formatSize(v.size)}</span>
                        <span className="text-xs text-muted-foreground">{formatDateTime(v.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Change status dialog */}
      <Dialog open={statusChangeOpen} onOpenChange={setStatusChangeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alterar status do documento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Novo status</label>
              <select value={statusForm.status_key} onChange={(e) => setStatusForm({ ...statusForm, status_key: e.target.value })}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm mt-1" data-testid="status-select">
                {statuses.map((s) => <option key={s.id} value={s.key}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Justificativa / observação</label>
              <Textarea rows={3} value={statusForm.justification} onChange={(e) => setStatusForm({ ...statusForm, justification: e.target.value })} data-testid="status-justification" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChangeOpen(false)}>Cancelar</Button>
            <Button onClick={changeStatus} data-testid="status-confirm-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit project dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl" aria-describedby="edit-project-desc">
          <DialogHeader>
            <DialogTitle>Editar projeto</DialogTitle>
            <p id="edit-project-desc" className="sr-only">Atualize os dados e o status do projeto</p>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-project-form-name" />
            </div>
            <div>
              <Label className="text-xs">Código</Label>
              <Input value={editForm.code || ""} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} data-testid="edit-project-form-code" />
            </div>
            <div>
              <Label className="text-xs">Responsável BAUEN</Label>
              <Input value={editForm.responsible || ""} onChange={(e) => setEditForm({ ...editForm, responsible: e.target.value })} data-testid="edit-project-form-responsible" />
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Input value={editForm.category || ""} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} data-testid="edit-project-form-category" />
            </div>
            <div>
              <Label className="text-xs">Data início</Label>
              <Input type="date" value={editForm.start_date || ""} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} data-testid="edit-project-form-start" />
            </div>
            <div>
              <Label className="text-xs">Prazo</Label>
              <Input type="date" value={editForm.due_date || ""} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} data-testid="edit-project-form-due" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Descrição</Label>
              <Textarea rows={3} value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} data-testid="edit-project-form-description" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={editForm.status || "em_andamento"} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm" data-testid="edit-project-form-status">
                <option value="em_andamento">Em andamento</option>
                <option value="aguardando">Aguardando</option>
                <option value="finalizado">Finalizado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={saveEditProject} disabled={!editForm.name?.trim() || editSaving} data-testid="edit-project-form-save">
              {editSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando…</> : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm mt-0.5">{children}</p>
    </div>
  );
}

function CategoryView({ docs, categories, isAdmin, openDoc, download, openUpload, onlyCat }) {
  if (docs.length === 0) return <Card><CardContent className="p-10 text-sm text-muted-foreground text-center">Nenhum documento{onlyCat ? " nesta categoria" : ""}.</CardContent></Card>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {docs.map((d) => {
        const cat = categories.find((c) => c.id === d.category_id);
        return (
          <div key={d.id} className="card-lift bg-card border rounded-xl p-4 flex flex-col" data-testid={`doc-card-${d.id}`}>
            <button onClick={() => openDoc(d)} className="text-left flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{d.filename}</p>
                </div>
                <FileText size={16} className="text-muted-foreground shrink-0" />
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <StatusBadge statusKey={d.status_key} />
                {cat && <span className="text-[10px] uppercase tracking-widest" style={cat.color ? { color: cat.color, fontWeight: 600 } : { color: "var(--muted-foreground)" }}>{cat.name}</span>}
              </div>
            </button>
            <div className="mt-3 pt-3 border-t flex items-center gap-1 justify-between text-xs">
              <span className="text-muted-foreground">{formatSize(d.size)}</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => download(d.id, true)} title="Pré-visualizar" data-testid={`quick-preview-${d.id}`}><Eye size={13} /></Button>
                <Button variant="ghost" size="sm" onClick={() => download(d.id, false)} title="Download" data-testid={`quick-download-${d.id}`}><Download size={13} /></Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
